import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import TrailingSlashRedirect from "./components/TrailingSlashRedirect";
import CookieBanner from "./components/CookieBanner";
import GAPageTracker from "./components/GAPageTracker";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import DashboardPage from "./pages/DashboardPage";
import CompanyProfilePage from "./pages/CompanyProfilePage";
import TenderDetailPage from "./pages/TenderDetailPage";
import FavoritesPage from "./pages/FavoritesPage";
import PricingPage from "./pages/PricingPage";
import DCESuccessPage from "./pages/DCESuccessPage";
import SubscriptionSuccessPage from "./pages/SubscriptionSuccessPage";
import GuideAppelsOffresPage from "./pages/GuideAppelsOffresPage";
import BlogPage from "./pages/BlogPage";
import AdminPage from "./pages/AdminPage";
import ContactPage from "./pages/ContactPage";
import LegalPage from "./pages/LegalPage";
import NotFoundPage from "./pages/NotFoundPage";
import SyntheseMarchesPage from "./pages/SyntheseMarchesPage";
import SyntheseRapidePage from "./pages/SyntheseRapidePage";
import SynthesePublicPage from "./pages/SynthesePublicPage";
import TarifsSynthesePage from "./pages/TarifsSynthesePage";
import GuideDCEArticle from "./pages/blog/GuideDCEArticle";
import SourcesMarchesArticle from "./pages/blog/SourcesMarchesArticle";
import SeuilsMarchesArticle from "./pages/blog/SeuilsMarchesArticle";
import IAMarchesArticle from "./pages/blog/IAMarchesArticle";
import RepondreAppelOffreArticle from "./pages/blog/RepondreAppelOffreArticle";
import PMEMarchesArticle from "./pages/blog/PMEMarchesArticle";
// New 2026 Trend Articles
import CritereEnvironnementalArticle from "./pages/blog/CritereEnvironnementalArticle";
import NouveauxSeuilsArticle from "./pages/blog/NouveauxSeuilsArticle";
import IACommandePubliqueArticle from "./pages/blog/IACommandePubliqueArticle";
import FacturationElectroniqueArticle from "./pages/blog/FacturationElectroniqueArticle";
import PMESimplificationArticle from "./pages/blog/PMESimplificationArticle";
import DefaillancesSecurisationArticle from "./pages/blog/DefaillancesSecurisationArticle";
// New Strategic SEO Articles
import CommentTrouverAppelsOffresArticle from "./pages/blog/CommentTrouverAppelsOffresArticle";
import CommentLireDCEArticle from "./pages/blog/CommentLireDCEArticle";
import CommentAnalyserDCEArticle from "./pages/blog/CommentAnalyserDCEArticle";
import CommentRepondreAppelOffreArticle from "./pages/blog/CommentRepondreAppelOffreArticle";
import CommentRedigerMemoireTechniqueArticle from "./pages/blog/CommentRedigerMemoireTechniqueArticle";
import RCvsCCTPvsCCAPArticle from "./pages/blog/RCvsCCTPvsCCAPArticle";
import CriteresAttributionArticle from "./pages/blog/CriteresAttributionArticle";
import MAPADefinitionArticle from "./pages/blog/MAPADefinitionArticle";
import RSEMarchesPublicsArticle from "./pages/blog/RSEMarchesPublicsArticle";
import MarchesPublicsBatimentArticle from "./pages/blog/MarchesPublicsBatimentArticle";
import BilanCarboneMarchesPublicsArticle from "./pages/blog/BilanCarboneMarchesPublicsArticle";
import DefaillancesSecurisationMarchesPublics2025Article from "./pages/blog/DefaillancesSecurisationMarchesPublics2025Article";
import EviterErreursAppelOffresArticle from "./pages/blog/EviterErreursAppelOffresArticle";
import GarantiesMarchesPublicsArticle from "./pages/blog/GarantiesMarchesPublicsArticle";
import DematerialisationMarchesPublicsArticle from "./pages/blog/DematerialisationMarchesPublicsArticle";
import ClausesSocialesRSEArticle from "./pages/blog/ClausesSocialesRSEArticle";
import IAGenerativePMEArticle from "./pages/blog/IAGenerativePMEArticle";
import CADAOpenDataArticle from "./pages/blog/CADAOpenDataArticle";

// SEO Pages - New URLs
import VeilleMarchesPublicsPage from "./pages/VeilleMarchesPublicsPage";
import TrouverAppelsOffresPage from "./pages/TrouverAppelsOffresPage";
import AnalyseDcePage from "./pages/AnalyseDcePage";
import PlateformeAppelsOffresPage from "./pages/PlateformeAppelsOffresPage";
import RepondreMarchesPublicsPage from "./pages/RepondreMarchesPublicsPage";

// Sector-specific SEO Pages
import MarchesPublicsBtpPage from "./pages/MarchesPublicsBtpPage";
import MarchesPublicsSecuritePage from "./pages/MarchesPublicsSecuritePage";
import MarchesPublicsInformatiquePage from "./pages/MarchesPublicsInformatiquePage";
import MarchesPublicsNettoyagePage from "./pages/MarchesPublicsNettoyagePage";
import MarchesPublicsFormationPage from "./pages/MarchesPublicsFormationPage";
import MarchesPublicsMaintenancePage from "./pages/MarchesPublicsMaintenancePage";
import MarchesPublicsTransportPage from "./pages/MarchesPublicsTransportPage";
import MarchesPublicsRestaurationPage from "./pages/MarchesPublicsRestaurationPage";

// Settings
import SettingsPage from "./pages/SettingsPage";

// Legacy SEO Pages (for 301 redirects)
import LogicielAppelsOffresPage from "./pages/LogicielAppelsOffresPage";
import TrouverAppelsOffresPmePage from "./pages/TrouverAppelsOffresPmePage";
import AnalyseDceIaPage from "./pages/AnalyseDceIaPage";
import VeilleBoampPage from "./pages/VeilleBoampPage";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-violet-900 via-indigo-900 to-slate-900">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <TrailingSlashRedirect />
          <GAPageTracker />
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/legal" element={<LegalPage />} />
          <Route path="/analyse-dce" element={<SyntheseRapidePage />} />
          <Route path="/analyse-gratuite" element={<SynthesePublicPage />} />
          
          {/* Tarifs Synthèse - Protected (inside user account only) */}
          <Route path="/tarifs-synthese" element={
            <ProtectedRoute>
              <TarifsSynthesePage />
            </ProtectedRoute>
          } />
          <Route path="/mentions-legales" element={<LegalPage />} />
          <Route path="/guide-appels-offres" element={<GuideAppelsOffresPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/guide-dce" element={<GuideDCEArticle />} />
          <Route path="/blog/sources-marches-publics" element={<SourcesMarchesArticle />} />
          <Route path="/blog/seuils-marches-publics-2026" element={<SeuilsMarchesArticle />} />
          <Route path="/blog/ia-marches-publics" element={<IAMarchesArticle />} />
          <Route path="/blog/repondre-appel-offre" element={<RepondreAppelOffreArticle />} />
          <Route path="/blog/pme-marches-publics" element={<PMEMarchesArticle />} />
          {/* New 2026 Trend Articles */}
          <Route path="/blog/critere-environnemental-2026" element={<CritereEnvironnementalArticle />} />
          <Route path="/blog/nouveaux-seuils-2026" element={<NouveauxSeuilsArticle />} />
          <Route path="/blog/ia-commande-publique-2026" element={<IACommandePubliqueArticle />} />
          <Route path="/blog/facturation-electronique-2026" element={<FacturationElectroniqueArticle />} />
          <Route path="/blog/pme-simplification-2026" element={<PMESimplificationArticle />} />
          <Route path="/blog/defaillances-securisation-2026" element={<DefaillancesSecurisationArticle />} />
          {/* New Strategic SEO Blog Articles */}
          <Route path="/blog/comment-trouver-appels-offres" element={<CommentTrouverAppelsOffresArticle />} />
          <Route path="/blog/comment-lire-dce-rapidement" element={<CommentLireDCEArticle />} />
          <Route path="/blog/comment-analyser-dce" element={<CommentAnalyserDCEArticle />} />
          <Route path="/blog/comment-repondre-appel-offres" element={<CommentRepondreAppelOffreArticle />} />
          <Route path="/blog/comment-rediger-memoire-technique" element={<CommentRedigerMemoireTechniqueArticle />} />
          <Route path="/blog/rc-vs-cctp-vs-ccap" element={<RCvsCCTPvsCCAPArticle />} />
          <Route path="/blog/criteres-attribution-marches-publics" element={<CriteresAttributionArticle />} />
          <Route path="/blog/mapa-definition-fonctionnement" element={<MAPADefinitionArticle />} />
          <Route path="/blog/rse-marches-publics-guide" element={<RSEMarchesPublicsArticle />} />
          <Route path="/blog/marches-publics-batiment-btp" element={<MarchesPublicsBatimentArticle />} />
          <Route path="/blog/bilan-carbone-marches-publics" element={<BilanCarboneMarchesPublicsArticle />} />
          <Route path="/blog/defaillances-securisation-marches-publics-2025" element={<DefaillancesSecurisationMarchesPublics2025Article />} />
          <Route path="/blog/eviter-erreurs-appel-offres" element={<EviterErreursAppelOffresArticle />} />
          <Route path="/blog/garanties-marches-publics" element={<GarantiesMarchesPublicsArticle />} />
          <Route path="/blog/dematerialisation-marches-publics" element={<DematerialisationMarchesPublicsArticle />} />
          <Route path="/blog/clauses-sociales-rse-marches-publics" element={<ClausesSocialesRSEArticle />} />
          <Route path="/blog/ia-generative-pme-marches-publics" element={<IAGenerativePMEArticle />} />
          <Route path="/blog/cada-open-data-marches-publics" element={<CADAOpenDataArticle />} />
          
          {/* SEO Pillar Pages - New Structure */}
          <Route path="/veille-marches-publics" element={<VeilleMarchesPublicsPage />} />
          <Route path="/trouver-appels-offres" element={<TrouverAppelsOffresPage />} />
          <Route path="/guide-analyse-dce" element={<AnalyseDcePage />} />
          <Route path="/plateforme-appels-offres" element={<PlateformeAppelsOffresPage />} />
          <Route path="/repondre-marches-publics" element={<RepondreMarchesPublicsPage />} />
          
          {/* Sector-specific SEO Pages */}
          <Route path="/marches-publics-btp" element={<MarchesPublicsBtpPage />} />
          <Route path="/marches-publics-securite" element={<MarchesPublicsSecuritePage />} />
          <Route path="/marches-publics-informatique" element={<MarchesPublicsInformatiquePage />} />
          <Route path="/marches-publics-nettoyage" element={<MarchesPublicsNettoyagePage />} />
          <Route path="/marches-publics-formation" element={<MarchesPublicsFormationPage />} />
          <Route path="/marches-publics-maintenance" element={<MarchesPublicsMaintenancePage />} />
          <Route path="/marches-publics-transport" element={<MarchesPublicsTransportPage />} />
          <Route path="/marches-publics-restauration" element={<MarchesPublicsRestaurationPage />} />
          
          {/* 301 Redirects - Old URLs to New URLs */}
          <Route path="/trouver-appels-offres-pme" element={<Navigate to="/trouver-appels-offres" replace />} />
          <Route path="/analyse-dce-ia" element={<Navigate to="/analyse-dce" replace />} />
          <Route path="/logiciel-appels-offres" element={<Navigate to="/plateforme-appels-offres" replace />} />
          <Route path="/veille-boamp" element={<Navigate to="/veille-marches-publics" replace />} />
          
          {/* 404 - Catch all unknown routes */}
          <Route path="*" element={<NotFoundPage />} />
          
          {/* Synthese Marches - Protected */}
          <Route path="/synthese-marches" element={
            <ProtectedRoute>
              <SyntheseMarchesPage />
            </ProtectedRoute>
          } />
          
          {/* Admin Route */}
          <Route path="/admin" element={
            <ProtectedRoute>
              <AdminPage />
            </ProtectedRoute>
          } />
          
          {/* Protected Routes */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          } />
          <Route path="/settings" element={
            <ProtectedRoute>
              <SettingsPage />
            </ProtectedRoute>
          } />
          <Route path="/profile" element={
            <ProtectedRoute>
              <CompanyProfilePage />
            </ProtectedRoute>
          } />
          <Route path="/tender/:id" element={
            <ProtectedRoute>
              <TenderDetailPage />
            </ProtectedRoute>
          } />
          <Route path="/favorites" element={
            <ProtectedRoute>
              <FavoritesPage />
            </ProtectedRoute>
          } />
          <Route path="/dce/success" element={
            <ProtectedRoute>
              <DCESuccessPage />
            </ProtectedRoute>
          } />
          <Route path="/subscription/success" element={
            <ProtectedRoute>
              <SubscriptionSuccessPage />
            </ProtectedRoute>
          } />
        </Routes>
        <Toaster position="top-right" richColors />
        <CookieBanner />
      </BrowserRouter>
    </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
